/**
 * Package-prefix authorization (plan §4) — who may publish a given package id.
 *
 * The pure functions here settle "is this team allowed to publish this package id?" and
 * nothing else. What makes their answer meaningful is a separate check: at finalize the API
 * reads the id back out of the artifact itself (`apps/api/src/lib/artifact.ts`), so a team cannot claim
 * `com.acme`, declare `com.acme.game` and upload a manifest saying `com.google.anything`.
 * Without that, everything below is a naming convention rather than a boundary.
 *
 * They live in `shared` because the API enforces them, the CLI pre-checks them for a better
 * error message, and the tests exercise them directly.
 *
 * Comparison is always on dot boundaries: `com.acme` blocks `com.acme.games`, but leaves
 * `com.acmetools` alone. It is also case-insensitive — but only the *comparison* is. A package
 * id is stored exactly as its author typed it, because it is their name for their app and
 * Android reads it byte for byte out of the manifest.
 */
/**
 * A package segment: starts with a letter, then letters/digits/underscore.
 *
 * Uppercase is allowed because Android allows it. `com.acme.MyGame` is a real application id
 * that installs perfectly well, and Unity writes whatever Player Settings says, so rejecting
 * it would reject builds that are not wrong about anything.
 */
const SEGMENT = /^[a-zA-Z][a-zA-Z0-9_]*$/;
/**
 * Namespaces nobody gets to claim by self-service. Impersonating these is the cheapest
 * possible attack on a private distribution system: a build published as `com.google.*`
 * looks like a system component to anyone reading an install prompt.
 *
 * An instance owner can still insert a `package_prefixes` row by hand — the denylist gates
 * the claim endpoint, not the database.
 */
export const PREFIX_DENYLIST = [
    'android',
    'androidx',
    // BoardFlight's own client. A build published under it would be offered to Boards as the
    // client itself.
    'app.boardflight',
    'com.amazon',
    'com.android',
    'com.apple',
    'com.epicgames',
    'com.facebook',
    'com.google',
    'com.htc',
    'com.huawei',
    'com.meta',
    'com.microsoft',
    'com.oculus',
    'com.oneplus',
    'com.qualcomm',
    'com.samsung',
    'com.sec',
    'com.unity3d',
    'com.valvesoftware',
    'com.xiaomi',
    // Board's own: the Board Browser host every web game runs inside, and Board's game namespace.
    'co.harrishill',
    'dalvik',
    'fun.board',
    'java',
    'javax',
    'kotlin',
    'org.chromium',
    'org.godotengine',
    'org.khronos',
    'sun',
];
/**
 * Trimmed, and nothing else — this is the form a package id is *stored* as.
 *
 * It used to lowercase, which meant the server quietly published an app under a name its
 * author never typed: someone who declared `com.acme.MyGame` in Player Settings got
 * `com.acme.mygame` back, and every screen showed them a package id that was not theirs.
 * Whatever they chose is what BoardFlight records.
 */
export function normalizePackageName(input) {
    return input.trim();
}
/**
 * The form two package ids are compared *by*, and never the form either is stored as.
 *
 * Authorization is case-insensitive so that a team owning `com.acme` cannot be walked around
 * with `com.Acme`. Folding both sides of every comparison is what makes that safe: it can
 * only ever merge ids that already sit inside one namespace, and never lets a package escape
 * into a prefix some other team owns.
 */
export function foldPackageName(input) {
    return input.trim().toLowerCase();
}
/**
 * Structural validity for a package name or a prefix.
 *
 * At least two segments: a one-segment prefix such as `com` would swallow most of the
 * namespace on a first-come basis, and a one-segment package name is not a thing Android
 * accepts in practice.
 */
export function isValidPackageName(value) {
    if (value.length === 0 || value.length > 255)
        return false;
    const segments = value.split('.');
    if (segments.length < 2)
        return false;
    return segments.every((s) => SEGMENT.test(s));
}
/** Prefixes and package names share the same grammar. */
export const isValidPrefix = isValidPackageName;
/**
 * The narrower grammar a **web** app's package id has to meet — which is not ours.
 *
 * `@board.fun/web-pack` is the only thing that can produce a `.webapp.zip`, and it refuses to
 * pack an id that is not lowercase reverse-domain, at most 128 characters:
 *
 *   packageId "com.acme.MyGame" is not a valid reverse-domain id (e.g. fun.board.snakeworms)
 *
 * Read out of `PACKAGE_ID_RE` in `@board.fun/web-pack@1.0.0-beta.3`, which names it "the v1
 * packageId format" (docs/board-api.md). It is deliberately *not* folded into `SEGMENT` above:
 * `com.acme.MyGame` remains a perfectly real APK, so the rule is about what a web build can be
 * packed as and applies nowhere else.
 *
 * Asked before an id is written down rather than at deploy, because a package id cannot be
 * changed afterwards — an app that fails this would carry a Deploy button that could never
 * work, and say so a minute in, in the voice of a tool the developer never invoked.
 */
const WEB_SEGMENT = /^[a-z][a-z0-9_]*$/;
export function isValidWebPackageId(value) {
    if (!isValidPackageName(value) || value.length > 128)
        return false;
    return value.split('.').every((segment) => WEB_SEGMENT.test(segment));
}
/** The nearest id `web-pack` would take, or `null` when case was not the only problem. */
export function suggestWebPackageId(value) {
    const lowered = foldPackageName(value);
    return isValidWebPackageId(lowered) ? lowered : null;
}
/**
 * Why this id cannot belong to a web app, in a sentence meant for the person who typed it, or
 * `null` when it can. Written once here because the API refuses on it and the dashboard says
 * so before submitting, and those two must not drift.
 */
export function webPackageIdProblem(value) {
    const packageId = normalizePackageName(value);
    if (isValidWebPackageId(packageId))
        return null;
    const suggestion = suggestWebPackageId(packageId);
    return (`"${packageId}" cannot be a web app's package id: Board's packer only accepts lowercase ` +
        `reverse-domain ids. ` +
        (suggestion !== null
            ? `Use "${suggestion}" instead.`
            : `Use at least two dot-separated segments of lowercase letters, digits and underscores, ` +
                `each starting with a letter, for example "com.acme.pinball".`));
}
/**
 * Does `prefix` cover `packageName`? Exact match, or a proper dot-boundary descendant.
 *
 * `prefixCovers('com.acme', 'com.acme')` -> true
 * `prefixCovers('com.acme', 'com.acme.games')` -> true
 * `prefixCovers('com.acme', 'com.acmetools')` -> false
 */
export function prefixCovers(prefix, packageName) {
    const owned = foldPackageName(prefix);
    const candidate = foldPackageName(packageName);
    return candidate === owned || candidate.startsWith(`${owned}.`);
}
/**
 * Two prefixes conflict when either one covers the other. Used to reject a claim that
 * would overlap a prefix already owned by a different team, in either direction —
 * claiming `com.acme` when someone owns `com.acme.games` is just as much a collision as
 * the reverse.
 */
export function prefixesConflict(a, b) {
    return prefixCovers(a, b) || prefixCovers(b, a);
}
/** The denylist entry that blocks this prefix, or `null` if it is claimable. */
export function deniedBy(prefix) {
    return PREFIX_DENYLIST.find((entry) => prefixesConflict(entry, prefix)) ?? null;
}
/** True when at least one of the team's prefixes covers `packageName`. */
export function isCoveredByAny(prefixes, packageName) {
    return prefixes.some((prefix) => prefixCovers(prefix, packageName));
}
/**
 * Full claim check against a set of prefixes already owned by *other* teams.
 * Returns `null` when the claim is allowed.
 */
export function checkPrefixClaim(rawPrefix, otherTeamsPrefixes) {
    const prefix = normalizePackageName(rawPrefix);
    if (!isValidPrefix(prefix)) {
        return {
            code: 'invalid',
            message: 'A package prefix must be at least two dot-separated segments, each starting with a letter and containing only letters, digits and underscores (for example "com.acme").',
        };
    }
    const denied = deniedBy(prefix);
    if (denied !== null) {
        return {
            code: 'denied',
            message: `"${prefix}" overlaps the reserved namespace "${denied}" and cannot be claimed.`,
            entry: denied,
        };
    }
    const conflict = otherTeamsPrefixes.find((owned) => prefixesConflict(owned, prefix));
    if (conflict !== undefined) {
        return {
            code: 'conflict',
            message: `"${prefix}" overlaps "${conflict}", which is already claimed by another team.`,
            conflictsWith: conflict,
        };
    }
    return null;
}
//# sourceMappingURL=prefix.js.map